// Landing-page Focus — the pure half (no React, no fetch).
//
// Focus answers "what should this person see first?" on the platform
// overview. Three rules make it better than a filter:
//
//  1. It REORDERS, it never hides. Apps outside the focus stay on the
//     page in a second group, still loading, still reporting.
//  2. Critical items always break through. A critical alert from an app
//     outside the focus is shown, labelled as outside it.
//  3. It says what it left out. The page reports how many lower-severity
//     items sit in other areas, one click away.

import { FOCUS_AREAS, FOCUS_AREA_IDS, type FocusAreaId } from '@cyber-aware/shared-types';

export type FocusMode =
  | { kind: 'everything' }
  | { kind: 'leadership' }
  | { kind: 'areas'; areas: FocusAreaId[]; appKeys: ReadonlySet<string> };

export type FocusSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';
const RANK: Record<FocusSeverity, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };

/** Items the leadership view keeps: the few that matter most. */
export const LEADERSHIP_ITEM_LIMIT = 5;

const isFocusId = (x: string): x is FocusAreaId => (FOCUS_AREA_IDS as readonly string[]).includes(x);

/** Canonical form: valid, unique, in picker order, leadership alone. */
export function normalizeAreas(areas: readonly string[]): FocusAreaId[] {
  const set = new Set(areas.filter(isFocusId));
  if (set.has('leadership')) return ['leadership'];
  return FOCUS_AREA_IDS.filter((id) => set.has(id));
}

export function resolveFocus(areas: readonly FocusAreaId[] | null | undefined): FocusMode {
  const xs = normalizeAreas(areas ?? []);
  if (xs.length === 0) return { kind: 'everything' };
  if (xs[0] === 'leadership') return { kind: 'leadership' };
  const appKeys = new Set<string>();
  for (const a of FOCUS_AREAS) if (xs.includes(a.id)) for (const k of a.appKeys) appKeys.add(k);
  return { kind: 'areas', areas: xs, appKeys };
}

/** The apps a focus lifts; null when it treats every app alike. */
export function focusAppKeys(mode: FocusMode): ReadonlySet<string> | null {
  return mode.kind === 'areas' ? mode.appKeys : null;
}

export function isAppInFocus(mode: FocusMode, appKey: string): boolean {
  return mode.kind !== 'areas' || mode.appKeys.has(appKey);
}

/** Split apps into "your focus" and "other apps", keeping input order. */
export function splitApps<T extends { key: string }>(apps: readonly T[], mode: FocusMode): { focus: T[]; other: T[] } {
  const focus: T[] = [];
  const other: T[] = [];
  for (const a of apps) (isAppInFocus(mode, a.key) ? focus : other).push(a);
  return { focus, other };
}

export interface FocusableItem {
  appKey: string;
  severity: FocusSeverity;
  occurredAt: number;
}

export interface ArrangedAttention<T> {
  /** What the feed shows, most severe first, newest first within a severity. */
  shown: Array<T & { outsideFocus: boolean }>;
  /** Items in other areas not shown (everything outside the focus that is not critical). */
  hiddenOutside: number;
  /** Leadership view only: lower-priority items trimmed to keep it short. */
  trimmed: number;
}

export function sortBySeverity<T extends FocusableItem>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => RANK[b.severity] - RANK[a.severity] || b.occurredAt - a.occurredAt);
}

export function arrangeAttention<T extends FocusableItem>(
  items: readonly T[],
  mode: FocusMode,
  opts: { showOutside: boolean },
): ArrangedAttention<T> {
  const sorted = sortBySeverity(items);
  if (mode.kind === 'everything') {
    return { shown: sorted.map((i) => ({ ...i, outsideFocus: false })), hiddenOutside: 0, trimmed: 0 };
  }
  if (mode.kind === 'leadership') {
    const shown = sorted.slice(0, LEADERSHIP_ITEM_LIMIT).map((i) => ({ ...i, outsideFocus: false }));
    return { shown, hiddenOutside: 0, trimmed: sorted.length - shown.length };
  }
  const shown: Array<T & { outsideFocus: boolean }> = [];
  let hiddenOutside = 0;
  for (const i of sorted) {
    const inFocus = mode.appKeys.has(i.appKey);
    if (inFocus) shown.push({ ...i, outsideFocus: false });
    else if (i.severity === 'critical' || opts.showOutside) shown.push({ ...i, outsideFocus: true });
    else hiddenOutside++;
  }
  return { shown, hiddenOutside, trimmed: 0 };
}

/** Count per severity, for the filter chips. Zero counts are omitted. */
export function severityCounts(items: readonly FocusableItem[]): Array<{ severity: FocusSeverity; count: number }> {
  const out: Array<{ severity: FocusSeverity; count: number }> = [];
  for (const s of ['critical', 'high', 'medium', 'low', 'info'] as const) {
    const count = items.filter((i) => i.severity === s).length;
    if (count > 0) out.push({ severity: s, count });
  }
  return out;
}

/**
 * Propose a focus from evidence instead of asking cold. `appsWithData`
 * is every app whose tile reported real data; the reason is shown to the
 * person verbatim so the suggestion is never a mystery.
 */
export function suggestFocus(input: { appsWithData: readonly string[]; isOwner: boolean }): {
  areas: FocusAreaId[];
  reason: string;
} {
  const active = FOCUS_AREAS.filter((a) => a.id !== 'leadership' && a.appKeys.some((k) => input.appsWithData.includes(k)));
  if (active.length === 0) {
    return { areas: [], reason: 'No app has data yet, so everything stays in view.' };
  }
  if (active.length >= 4 && input.isOwner) {
    return {
      areas: ['leadership'],
      reason: `You own this organisation and ${active.length} areas have data, so the short overview fits best.`,
    };
  }
  if (active.length >= 4) {
    return { areas: [], reason: `${active.length} areas have data, so everything stays in view.` };
  }
  return {
    areas: normalizeAreas(active.map((a) => a.id)),
    reason: active.length === 1 ? 'This is the area where your apps have data.' : 'These are the areas where your apps have data.',
  };
}

/** `?focus=email,exposure` → ids. `all` or empty → Everything. No param → null. */
export function parseFocusParam(raw: string | null): FocusAreaId[] | null {
  if (raw === null) return null;
  const v = raw.trim().toLowerCase();
  if (v === '' || v === 'all') return [];
  return normalizeAreas(v.split(','));
}

export function formatFocusParam(areas: readonly FocusAreaId[]): string {
  const xs = normalizeAreas(areas);
  return xs.length === 0 ? 'all' : xs.join(',');
}

export function focusLabel(areas: readonly FocusAreaId[]): string {
  const xs = normalizeAreas(areas);
  if (xs.length === 0) return 'Everything';
  const names = xs.map((id) => FOCUS_AREAS.find((a) => a.id === id)!.label);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]} + ${names.length - 1} more`;
}

/** Picker behaviour: leadership is exclusive; areas combine freely. */
export function toggleArea(current: readonly FocusAreaId[], id: FocusAreaId): FocusAreaId[] {
  const xs = normalizeAreas(current);
  if (id === 'leadership') return xs[0] === 'leadership' ? [] : ['leadership'];
  const base = xs.filter((x) => x !== 'leadership');
  return normalizeAreas(base.includes(id) ? base.filter((x) => x !== id) : [...base, id]);
}

export function sameAreas(a: readonly FocusAreaId[] | null, b: readonly FocusAreaId[] | null): boolean {
  if (a === null || b === null) return a === b;
  const x = normalizeAreas(a);
  const y = normalizeAreas(b);
  return x.length === y.length && x.every((v, i) => v === y[i]);
}
